/*
  Still Suain navigation, October 2026.
  1. Desktop mega panels (snippets/header-mega-menu.liquid): open on hover after 300 ms, switch
     between panels after 120 ms, close 400 ms after the pointer leaves, so passing over the bar
     does not flash panels and the pointer can cross the gap to the panel (Baymard recommends
     300 to 500 ms). Enter and Space toggle through the native <details>; Escape closes an open
     panel from anywhere and returns focus when it was inside. Touch toggles on tap. A real mouse
     click on a label with data-ss-href (Shop All) goes to that page; with a modifier key it opens
     in a new tab.
  2. Menu drawer tabs (snippets/header-drawer.liquid): ARIA tabs with arrow, Home and End keys.
     Tabs are links, not buttons, because Sense's MenuDrawer binds every drawer button as a
     submenu close button.
  3. Desktop tile images load lazily and are fetched early once the pointer or focus reaches the menu.
     The drawer is text only.
*/
(() => {
  const OPEN_DELAY = 300;
  const SWITCH_DELAY = 120;
  const CLOSE_DELAY = 400;
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  const warmImages = (root) => {
    if (!root) return;
    root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  };

  const setOpen = (details, open) => {
    const summary = details.querySelector('summary');
    if (open) {
      details.setAttribute('open', '');
    } else {
      details.removeAttribute('open');
    }
    summary.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  const initMegaMenu = (nav) => {
    if (nav.dataset.ssReady) return;
    nav.dataset.ssReady = 'true';

    nav.addEventListener('pointerenter', () => warmImages(nav), { once: true });
    nav.addEventListener('focusin', () => warmImages(nav), { once: true });

    const states = [];
    const cancelAll = () =>
      states.forEach((state) => {
        clearTimeout(state.openTimer);
        clearTimeout(state.closeTimer);
      });

    Array.from(nav.querySelectorAll('header-menu')).forEach((menu) => {
      const details = menu.querySelector('details');
      const summary = details && details.querySelector('summary');
      if (!summary) return;

      const state = { details, summary, openTimer: null, closeTimer: null, openedByHover: false };
      states.push(state);
      let lastPointerType = '';
      let lastPointerTime = 0;

      const closeOthers = () => {
        states.forEach((other) => {
          if (other !== state && other.details.hasAttribute('open')) {
            setOpen(other.details, false);
            other.openedByHover = false;
          }
        });
      };

      menu.addEventListener('pointerenter', (event) => {
        if (!canHover.matches || event.pointerType !== 'mouse') return;
        clearTimeout(state.closeTimer);
        if (details.hasAttribute('open')) return;
        const anotherOpen = states.some((other) => other !== state && other.details.hasAttribute('open'));
        state.openTimer = setTimeout(
          () => {
            closeOthers();
            setOpen(details, true);
            state.openedByHover = true;
          },
          anotherOpen ? SWITCH_DELAY : OPEN_DELAY
        );
      });

      menu.addEventListener('pointerleave', (event) => {
        if (!canHover.matches || event.pointerType !== 'mouse') return;
        clearTimeout(state.openTimer);
        if (!details.hasAttribute('open')) return;
        // A panel opened from the keyboard stays while focus is inside it.
        if (!state.openedByHover && details.contains(document.activeElement)) return;
        state.closeTimer = setTimeout(() => {
          setOpen(details, false);
          state.openedByHover = false;
        }, CLOSE_DELAY);
      });

      summary.addEventListener('pointerdown', (event) => {
        lastPointerType = event.pointerType;
        lastPointerTime = Date.now();
      });

      // Bound after Sense's global.js handler, which flips aria-expanded on every summary click;
      // every branch here that cancels the native toggle restores it.
      summary.addEventListener('click', (event) => {
        cancelAll();
        const isMouse =
          canHover.matches && event.detail > 0 && lastPointerType === 'mouse' && Date.now() - lastPointerTime < 1000;
        const href = summary.dataset.ssHref;

        if (isMouse && href) {
          event.preventDefault();
          summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
          if (event.metaKey || event.ctrlKey || event.shiftKey) {
            window.open(href, '_blank', 'noopener');
          } else {
            window.location.href = href;
          }
          return;
        }

        // A mouse click on a label that hover has already opened keeps the panel open, still
        // treated as hover-opened so it closes when the pointer leaves.
        if (isMouse && state.openedByHover && details.hasAttribute('open')) {
          event.preventDefault();
          summary.setAttribute('aria-expanded', 'true');
          return;
        }

        state.openedByHover = false;
        if (!details.hasAttribute('open')) closeOthers();
      });
    });

    // WCAG 1.4.13: Escape dismisses an open panel wherever focus is.
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      states.forEach((state) => {
        if (!state.details.hasAttribute('open')) return;
        const focusWasInside = state.details.contains(document.activeElement);
        clearTimeout(state.openTimer);
        clearTimeout(state.closeTimer);
        setOpen(state.details, false);
        state.openedByHover = false;
        if (focusWasInside) state.summary.focus();
      });
    });
  };

  const initDrawerTabs = (tablist) => {
    if (tablist.dataset.ssReady) return;
    tablist.dataset.ssReady = 'true';

    const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
    const scroller = tablist.closest('.menu-drawer__navigation-container');
    const panelFor = (tab) => document.getElementById(tab.getAttribute('aria-controls'));

    const select = (tab, moveFocus) => {
      tabs.forEach((item) => {
        const selected = item === tab;
        const panel = panelFor(item);
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) {
          item.removeAttribute('tabindex');
        } else {
          item.setAttribute('tabindex', '-1');
        }
        if (panel) panel.hidden = !selected;
      });
      if (scroller) scroller.scrollTop = 0;
      if (moveFocus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        select(tab, false);
      });

      tab.addEventListener('keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
        else if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
        else if (event.key === 'Home') next = tabs[0];
        else if (event.key === 'End') next = tabs[tabs.length - 1];
        else if (event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          select(tab, false);
          return;
        }
        if (!next) return;
        event.preventDefault();
        select(next, true);
      });
    });
  };

  const init = () => {
    document.querySelectorAll('.ss-nav').forEach(initMegaMenu);
    document.querySelectorAll('[data-ss-tabs]').forEach(initDrawerTabs);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: the header section re-renders in place.
  document.addEventListener('shopify:section:load', init);
})();
